import CodeMirror from '@uiw/react-codemirror';
import { EditorView } from '@codemirror/view';

import { cn } from '@/lib/utils';

import { fileDrawerCodeMirrorTheme } from './codemirrorFileDrawerTheme';
import { codeMirrorLanguageExtensions, type CodeModuleLanguage } from './codeMirrorLanguageExtensions';

const editorTheme = EditorView.theme({
    '&': { fontSize: '13px', height: '100%' },
    '.cm-scroller': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' },
});

export type CodeModuleEditorThemePreset = 'default' | 'fileDrawer';

export interface CodeModuleEditorProps {
    value: string;
    language: CodeModuleLanguage;
    className?: string;
    /** `fileDrawer`: #000 background + brighter tokens (e.g. capability file drawer). */
    themePreset?: CodeModuleEditorThemePreset;
    /** When false, the document is editable and `onChange` should be provided for controlled usage. */
    readOnly?: boolean;
    onChange?: (value: string) => void;
}

export function CodeModuleEditor({
    value,
    language,
    className,
    themePreset = 'default',
    readOnly = true,
    onChange,
}: CodeModuleEditorProps) {
    const cmTheme = themePreset === 'fileDrawer' ? fileDrawerCodeMirrorTheme : 'dark';

    return (
        <div className={cn('flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden', className)}>
            <CodeMirror
                value={value}
                height="100%"
                className="box-border min-h-0 min-w-0 h-full flex-1 overflow-hidden [&_.cm-editor]:box-border [&_.cm-editor]:flex [&_.cm-editor]:h-full [&_.cm-editor]:min-h-0 [&_.cm-editor]:flex-col [&_.cm-editor]:outline-none [&_.cm-scroller]:min-h-0 [&_.cm-scroller]:flex-1"
                theme={cmTheme}
                extensions={[...codeMirrorLanguageExtensions(language), editorTheme]}
                editable={!readOnly}
                readOnly={readOnly}
                basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: !readOnly,
                }}
                onChange={readOnly ? undefined : onChange}
            />
        </div>
    );
}
